// src/MockEngine/MockScoring.js
// ------------------------------------------------------------
// MOCK ENGINE — SCORING (NO GENERATION HERE)
// - Takes a base ProjectInstance (from mockGenerateProject.js)
// - Computes: interface scores, orphan requirements, budget concentration,
//   stability index, concrete alerts (why/impact)
// - Returns a NEW ProjectInstance with kpis populated
// ------------------------------------------------------------

import n1Lib from "./n1-systems.json";
import n2Lib from "./n2-subsystems.json";

/** -----------------------------
 * Helpers
 * ------------------------------*/

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

function hasOverlap(aStart, aEnd, bStart, bEnd) {
  return aStart < bEnd && bStart < aEnd;
}

function budgetBand(minPct, maxPct) {
  const mid = (Number(minPct) + Number(maxPct)) / 2;
  if (mid >= 30) return "HIGH";
  if (mid >= 18) return "MEDIUM";
  return "LOW";
}

function computeInterfaceScore({ sourceCrit, targetCrit, ifaceCrit, overlap }) {
  // Minimal, explicable:
  // base = sum criticalities + overlap bonus
  const base = Number(sourceCrit) + Number(targetCrit) + Number(ifaceCrit);
  return base + (overlap ? 2 : 0);
}

/** -----------------------------
 * Main scoring entrypoint
 * ------------------------------*/

/**
 * Computes KPIs for a base ProjectInstance.
 *
 * @param {Object} projectInstance - output of mockGenerateProject()
 * @returns {Object} new ProjectInstance (same shape) with kpis filled
 */
export function applyMockScoring(projectInstance) {
  if (!projectInstance || !projectInstance.project || !projectInstance.activated) {
    throw new Error("Invalid ProjectInstance passed to applyMockScoring().");
  }

  const n1ById = Object.fromEntries((n1Lib.systemsN1 || []).map((x) => [x.id, x]));
  const n2ById = Object.fromEntries((n2Lib.subsystemsN2 || []).map((x) => [x.id, x]));

  const activatedN1 = projectInstance.activated.n1 || [];
  const activatedN2 = projectInstance.activated.n2 || [];

  // Planning windows map
  const windows = (projectInstance.planning?.n2Windows || []).reduce((acc, w) => {
    acc[w.n2Id] = w;
    return acc;
  }, {});

  // 1) Orphan requirements
  const orphanReqs = (projectInstance.requirements || []).filter(
    (r) => !r.linkedN2 || r.linkedN2.length === 0
  );

  // 2) Interface scoring + top interfaces
  const scoredInterfaces = (projectInstance.interfaces || []).map((itf) => {
    const s = n2ById[itf.sourceN2];
    const t = n2ById[itf.targetN2];

    const sW = windows[itf.sourceN2];
    const tW = windows[itf.targetN2];

    const overlap =
      sW && tW ? hasOverlap(sW.startMonth, sW.endMonth, tW.startMonth, tW.endMonth) : false;

    const score = computeInterfaceScore({
      sourceCrit: s?.criticality ?? 3,
      targetCrit: t?.criticality ?? 3,
      ifaceCrit: itf.criticality ?? 3,
      overlap,
    });

    return {
      ...itf,
      score,
      overlap,
    };
  });

  scoredInterfaces.sort((a, b) => b.score - a.score);

  // Thresholds (tunable)
  const criticalInterfacesCount = scoredInterfaces.filter((x) => x.score >= 14).length;
  const highInterfacesCount = scoredInterfaces.filter((x) => x.score >= 12 && x.score < 14).length;

  const topInterfaces = scoredInterfaces.slice(0, 5).map((x) => ({
    id: x.id,
    sourceN2: x.sourceN2,
    targetN2: x.targetN2,
    type: x.type,
    score: x.score,
  }));

  // 3) Budget concentration by N1 (simple: based on library N1 ranges)
  const budgetConcentration = activatedN1
    .map((n1Id) => {
      const n1 = n1ById[n1Id];
      if (!n1) return null;

      const band = budgetBand(n1.budgetMinPct, n1.budgetMaxPct);
      const message =
        band === "HIGH"
          ? `${n1.name} concentre une part budgétaire élevée (plage indicative).`
          : band === "MEDIUM"
            ? `${n1.name} représente un poste budgétaire significatif (plage indicative).`
            : `${n1.name} est un poste budgétaire faible (plage indicative).`;

      return { n1Id, band, message };
    })
    .filter(Boolean)
    .filter((x) => x.band !== "LOW");

  // 4) Project Stability Index (simple composite, explicable)
  let stability = 80;
  stability -= criticalInterfacesCount * 6;
  stability -= highInterfacesCount * 3;
  stability -= orphanReqs.length * 4;
  stability -= budgetConcentration.filter((x) => x.band === "HIGH").length * 3;
  stability = clamp(stability, 0, 100);

  // 5) Alerts (explicit: why + impact)
  const topAlerts = [];

  // Top interface alert
  if (scoredInterfaces[0]) {
    const itf = scoredInterfaces[0];
    const srcName = n2ById[itf.sourceN2]?.name ?? itf.sourceN2;
    const tgtName = n2ById[itf.targetN2]?.name ?? itf.targetN2;

    topAlerts.push({
      severity: itf.score >= 14 ? "CRITICAL" : "HIGH",
      title: `Interface prioritaire ${srcName} ↔ ${tgtName}`,
      why: `Coordination requise entre ${srcName} et ${tgtName} (interface ${itf.type}).`,
      impact: itf.overlap
        ? "Impact planning probable : fenêtres de réalisation se chevauchent."
        : "Impact planning possible : dépendances à confirmer.",
    });
  }

  // Budget concentration alert
  const highBudget = budgetConcentration.find((x) => x.band === "HIGH");
  if (highBudget) {
    const n1Name = n1ById[highBudget.n1Id]?.name ?? highBudget.n1Id;
    topAlerts.push({
      severity: "HIGH",
      title: `Concentration budgétaire sur ${n1Name}`,
      why: "Poste dominant dans l'estimatif (plage indicative).",
      impact: "Sensibilité élevée : dérive CAPEX probable si spécifications tardives / interfaces instables.",
    });
  }

  // Orphan requirements alert
  if (orphanReqs.length > 0) {
    topAlerts.push({
      severity: orphanReqs.length >= 3 ? "HIGH" : "MEDIUM",
      title: `${orphanReqs.length} exigence(s) non rattachée(s)`,
      why: "Certaines exigences ne sont reliées à aucun sous-système (N2).",
      impact: "Risque d'oubli en conception/exécution ; rattacher en priorité.",
    });
  }

  // Keep max 5
  const alertsFinal = topAlerts.slice(0, 5);

  // Return a NEW object (no mutation)
  return {
    ...projectInstance,
    kpis: {
      projectStabilityIndex: stability,
      criticalInterfacesCount,
      highInterfacesCount,
      orphanRequirementsCount: orphanReqs.length,
      budgetConcentration,
      topInterfaces,
      topAlerts: alertsFinal,
    },
  };
}